import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ColumnAlignment, Row, TableInstance, TableRowProps } from 'react-table';
import { CustomerManagementGridItem } from 'shared/types/customerManagement';
import { useCustomerListState } from '../state/customerListState';
import { CustomerManagementTablePagination } from './customer-management-table-pagination';
import { useUserPermissions } from 'shared/hooks';
import { containerStyles, sortStyles } from 'customer-management/styles';

const getHeaderFlexAlignment = (align: ColumnAlignment) => {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

interface CustomerManagementTableProps {
  instance: TableInstance<CustomerManagementGridItem>;
}
export function CustomerManagementTable({ instance }: CustomerManagementTableProps) {
  const userPermissions = useUserPermissions();
  const hasCustomerDeletionRequestPermission = userPermissions.includes('abg-retail.customer.request');
  const hasCustomerDeletionApprovePermission = userPermissions.includes('abg-retail.customer.approve');
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = instance;

  const headers = headerGroups.map(headerGroup => (
    <TableRow {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers
        .filter(
          h =>
            (h.id !== 'isDeleted' && h.id !== 'isMarkedForDeletion') ||
            hasCustomerDeletionRequestPermission ||
            hasCustomerDeletionApprovePermission
        )
        .map(column => (
          <TableCell
            align={column.align}
            {...column.getHeaderProps({
              style: {
                width: column.width,
                minWidth: column.minWidth,
              },
            })}
          >
            <Box
              component='span'
              sx={sortStyles}
              {...column.getSortByToggleProps({ style: { justifyContent: getHeaderFlexAlignment(column.align) } })}
            >
              {column.render('Header')}
              {column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : null}
            </Box>
          </TableCell>
        ))}
    </TableRow>
  ));
  const rows = page.length
    ? page.map((row, i) => {
      prepareRow(row);
      const { key, ...rest } = row.getRowProps();
      return (
        <CustomerManagementTableRow
          key={key}
          row={row}
          showActions={hasCustomerDeletionRequestPermission || hasCustomerDeletionApprovePermission}
          {...rest}
        />
      );
    })
    : [
      <TableRow key='no-data'>
        <TableCell colSpan={14}>
          <Typography>No customers were found.</Typography>
        </TableCell>
      </TableRow>,
    ];
  return (
    <div>
      <TableContainer sx={containerStyles}>
        <Table size='small' stickyHeader {...getTableProps()}>
          <TableHead>{headers}</TableHead>
          <TableBody {...getTableBodyProps()}>{rows}</TableBody>
        </Table>
      </TableContainer>
      <CustomerManagementTablePagination instance={instance} />
    </div>
  );
}

interface CustomerManagementTableRowProps extends TableRowProps {
  row: Row<CustomerManagementGridItem>;
  showActions: boolean;
}

function CustomerManagementTableRow({ row, showActions, ...props }: CustomerManagementTableRowProps) {
  const state = useCustomerListState();

  const highlight = state.customerId.get() === row.id;
  return (
    <TableRow selected={highlight} {...props}>
      {row.cells
        .filter(r => (r.column.id !== 'isDeleted' && r.column.id !== 'isMarkedForDeletion') || showActions)
        .map(cell => (
          <TableCell align={cell.column.align} {...cell.getCellProps()}>
            {cell.render('Cell')}
          </TableCell>
        ))}
    </TableRow>
  );
}
